/**
 * MaintenanceSchedule — public calendar view of scheduled and active maintenance windows.
 *
 * Route: /status/maintenance
 *
 * Features:
 *   - Calendar view using react-big-calendar
 *   - List view on mobile (<768px)
 *   - Timezone selector (defaults to browser timezone)
 *   - Event click opens detail modal (affected_components, message, start/end)
 *   - Tooltip on hover with title and affected component count
 *   - Loading state: skeleton calendar
 *   - Empty state: "No scheduled maintenance" message
 *   - Keyboard navigation: Tab and Enter on calendar events
 *   - Fetches GET /api/status/maintenance?status=scheduled,active&limit=100
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, type Event as CalEvent, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { statusApi, type MaintenanceWindow } from '../api/status.js';

// ── date-fns localizer for react-big-calendar ─────────────────────────────────

const locales = {
  'en-US': undefined as unknown,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Australia/Sydney',
];

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

function formatInTz(isoString: string, timezone: string): string {
  try {
    return new Date(isoString).toLocaleString(undefined, {
      timeZone: timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return new Date(isoString).toLocaleString();
  }
}

function toCalendarDate(isoString: string, timezone: string): Date {
  // Convert UTC ISO string to a JS Date adjusted for the selected timezone
  // react-big-calendar works with local JS dates, so we shift them
  try {
    const utcDate = new Date(isoString);
    // Get the UTC time for the equivalent local time in the target timezone
    const tzOffset = new Date(utcDate.toLocaleString('en-US', { timeZone: timezone })).getTime() -
                     new Date(utcDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
    return new Date(utcDate.getTime() + tzOffset);
  } catch {
    return new Date(isoString);
  }
}

interface CalendarEvent extends CalEvent {
  resource: MaintenanceWindow;
}

function windowToCalendarEvent(win: MaintenanceWindow, timezone: string): CalendarEvent {
  const startAt = win.start_at ?? win.scheduled_start ?? '';
  const endAt = win.end_at ?? win.scheduled_end ?? '';
  return {
    title: win.title,
    start: toCalendarDate(startAt, timezone),
    end: toCalendarDate(endAt, timezone),
    resource: win,
  };
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function CalendarSkeleton() {
  return (
    <div className="animate-pulse space-y-2" aria-hidden="true">
      <div className="h-8 bg-neutral-200 rounded w-full" />
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 35 }).map((_, i) => (
          <div key={i} className="h-20 bg-neutral-100 rounded" />
        ))}
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

interface DetailModalProps {
  window: MaintenanceWindow;
  timezone: string;
  onClose: () => void;
}

function DetailModal({ window: win, timezone, onClose }: DetailModalProps) {
  const startAt = win.start_at ?? win.scheduled_start ?? '';
  const endAt = win.end_at ?? win.scheduled_end ?? '';
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Trap focus
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-label={`Maintenance window details: ${win.title}`}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 focus:outline-none"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-900">{win.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-700 transition-colors focus:outline-none focus:ring-1 focus:ring-neutral-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <dl className="space-y-3 text-sm">
          <div>
            <dt className="font-medium text-neutral-500 mb-0.5">Start</dt>
            <dd className="text-neutral-900">{formatInTz(startAt, timezone)}</dd>
          </div>
          <div>
            <dt className="font-medium text-neutral-500 mb-0.5">End</dt>
            <dd className="text-neutral-900">{formatInTz(endAt, timezone)}</dd>
          </div>
          {win.affected_components.length > 0 && (
            <div>
              <dt className="font-medium text-neutral-500 mb-1">Affected Components</dt>
              <dd className="flex flex-wrap gap-1">
                {win.affected_components.map((id) => (
                  <span key={id} className="bg-neutral-100 text-neutral-700 px-2 py-0.5 rounded text-xs">
                    {id}
                  </span>
                ))}
              </dd>
            </div>
          )}
          {win.message && (
            <div>
              <dt className="font-medium text-neutral-500 mb-0.5">Message</dt>
              <dd className="text-neutral-700 whitespace-pre-wrap">{win.message}</dd>
            </div>
          )}
          <div>
            <dt className="font-medium text-neutral-500 mb-0.5">Status</dt>
            <dd>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                win.status === 'active' || win.status === 'in_progress'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {win.status === 'in_progress' ? 'Active' : win.status.charAt(0).toUpperCase() + win.status.slice(1)}
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// ── List View (Mobile) ────────────────────────────────────────────────────────

interface ListViewProps {
  windows: MaintenanceWindow[];
  timezone: string;
  onSelect: (win: MaintenanceWindow) => void;
}

function ListView({ windows, timezone, onSelect }: ListViewProps) {
  if (windows.length === 0) {
    return (
      <p className="text-center text-neutral-500 py-12">No scheduled maintenance</p>
    );
  }

  return (
    <ul className="space-y-3" role="list">
      {windows.map((win) => {
        const startAt = win.start_at ?? win.scheduled_start ?? '';
        const endAt = win.end_at ?? win.scheduled_end ?? '';
        return (
          <li key={win.id}>
            <button
              type="button"
              className="w-full text-left border border-neutral-200 rounded-lg p-4 hover:border-neutral-300 hover:bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-colors"
              onClick={() => onSelect(win)}
              aria-label={`View details for ${win.title}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="font-medium text-neutral-900 text-sm">{win.title}</span>
                <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                  win.status === 'active' || win.status === 'in_progress'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {win.status === 'in_progress' ? 'Active' : win.status.charAt(0).toUpperCase() + win.status.slice(1)}
                </span>
              </div>
              <div className="text-xs text-neutral-500">
                {formatInTz(startAt, timezone)} – {formatInTz(endAt, timezone)}
              </div>
              {win.affected_components.length > 0 && (
                <div className="text-xs text-neutral-400 mt-1">
                  {win.affected_components.length} component{win.affected_components.length !== 1 ? 's' : ''} affected
                </div>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MaintenanceSchedulePage() {
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWindow, setSelectedWindow] = useState<MaintenanceWindow | null>(null);
  const [timezone, setTimezone] = useState(getBrowserTimezone);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [date, setDate] = useState(() => new Date());
  const [view, setView] = useState<string>(Views.MONTH);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Responsive: detect mobile
  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchWindows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await statusApi.getMaintenanceWindows({
        status: 'scheduled,active',
        limit: 100,
      });
      if (mountedRef.current) {
        setWindows(result.maintenance_windows);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError('Failed to load maintenance windows. Please try again.');
        console.error('[MaintenanceSchedule] fetch error:', err);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWindows();
  }, [fetchWindows]);

  const calendarEvents = windows.map((w) => windowToCalendarEvent(w, timezone));

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">Maintenance Schedule</h1>

        {/* Timezone selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="timezone-select" className="text-sm text-neutral-600">
            Timezone:
          </label>
          <select
            id="timezone-select"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="text-sm border border-neutral-300 rounded-md px-2 py-1 bg-white text-neutral-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
            aria-label="Select timezone for displaying maintenance times"
          >
            {/* Always show browser TZ at top if not in list */}
            {!COMMON_TIMEZONES.includes(getBrowserTimezone()) && (
              <option value={getBrowserTimezone()}>
                {getBrowserTimezone()} (Browser)
              </option>
            )}
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" role="alert">
          {error}
          <button
            type="button"
            onClick={fetchWindows}
            className="ml-2 underline hover:no-underline focus:outline-none"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <CalendarSkeleton />
      ) : isMobile ? (
        /* Mobile: list view */
        <ListView windows={windows} timezone={timezone} onSelect={setSelectedWindow} />
      ) : (
        /* Desktop: calendar view */
        <div style={{ height: '600px' }}>
          {windows.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-neutral-500">No scheduled maintenance</p>
            </div>
          ) : (
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              date={date}
              onNavigate={setDate}
              view={view as Parameters<typeof Calendar>[0]['view']}
              onView={(v) => setView(v)}
              onSelectEvent={(event) => {
                const calEv = event as CalendarEvent;
                setSelectedWindow(calEv.resource);
              }}
              eventPropGetter={(event) => {
                const calEv = event as CalendarEvent;
                const isActive = calEv.resource.status === 'active' ||
                                 calEv.resource.status === 'in_progress';
                return {
                  style: {
                    backgroundColor: isActive ? '#fef2f2' : '#eff6ff',
                    borderColor: isActive ? '#fca5a5' : '#93c5fd',
                    color: isActive ? '#991b1b' : '#1e40af',
                    border: '1px solid',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    padding: '2px 4px',
                  },
                };
              }}
              components={{
                event: ({ event }) => {
                  const calEv = event as CalendarEvent;
                  const count = calEv.resource.affected_components.length;
                  return (
                    <div
                      tabIndex={0}
                      role="button"
                      aria-label={`${calEv.title} — ${count} component${count !== 1 ? 's' : ''} affected`}
                      title={`${calEv.title}\n${count} component${count !== 1 ? 's' : ''} affected`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setSelectedWindow(calEv.resource);
                        }
                      }}
                      className="truncate text-xs font-medium cursor-pointer"
                    >
                      {calEv.title}
                    </div>
                  );
                },
              }}
              aria-label="Maintenance schedule calendar"
            />
          )}
        </div>
      )}

      {/* Detail modal */}
      {selectedWindow && (
        <DetailModal
          window={selectedWindow}
          timezone={timezone}
          onClose={() => setSelectedWindow(null)}
        />
      )}
    </div>
  );
}
